import { Edge } from '@xyflow/react';
import NodeData, {
  NodeDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import { ProjectJsonData } from 'src/types/project';

type ProjectEdge = Edge & { nodeId?: string };

/** legacyType → targetType — add more entries here when needed. */
const LEGACY_NODE_TYPE_MAP: Record<string, NodeDataType> = {
  'dest-sign': NodeDataType.DestinationSign,
};

function migrateNodeId(
  oldId: string,
  fromType: string,
  toType: NodeDataType,
): string {
  const prefix = `${fromType}-`;
  const suffix = oldId.startsWith(prefix) ? oldId.slice(prefix.length) : oldId;
  return `${toType}-${suffix}`;
}

function migrateCategory(
  category: NodeCategoryModel,
  targetType: NodeDataType,
): NodeCategoryModel {
  const base = NodeData[targetType];
  return {
    ...base,
    ...category,
    type: base.type,
    ports: base.ports,
    portIndexes: base.portIndexes,
    image: base.image,
    imageSidebar: base.imageSidebar,
    viewSize: category.viewSize ?? base.viewSize,
  };
}

function replaceNodeId(value: string, oldId: string, newId: string): string {
  if (!value || oldId === newId) return value;
  return value.split(oldId).join(newId);
}

/** Maps legacy node types to current NodeDataType values for older project JSON exports. */
export function migrateProjectJsonData(data: ProjectJsonData): ProjectJsonData {
  const idMap = new Map<string, string>();

  const nodes = data.nodes.map((node) => {
    const fromType = String(node.type || node.data?.key);
    const targetType = LEGACY_NODE_TYPE_MAP[fromType];
    if (!targetType) return node;

    const newId = migrateNodeId(node.id, fromType, targetType);
    idMap.set(node.id, newId);

    return {
      ...node,
      id: newId,
      type: targetType,
      data: {
        ...node.data,
        key: targetType,
        label: NodeData[targetType].name,
        category: migrateCategory(node.data.category, targetType),
      },
    };
  });

  if (!idMap.size) return data;

  const edges = (data.edges ?? []).map((edge) => {
    let next: ProjectEdge = { ...edge };

    idMap.forEach((newId, oldId) => {
      next = {
        ...next,
        id: replaceNodeId(next.id, oldId, newId),
        source: next.source === oldId ? newId : next.source,
        target: next.target === oldId ? newId : next.target,
        sourceHandle: next.sourceHandle
          ? replaceNodeId(next.sourceHandle, oldId, newId)
          : next.sourceHandle,
        targetHandle: next.targetHandle
          ? replaceNodeId(next.targetHandle, oldId, newId)
          : next.targetHandle,
      };
    });

    if (next.nodeId && LEGACY_NODE_TYPE_MAP[next.nodeId]) {
      next.nodeId = LEGACY_NODE_TYPE_MAP[next.nodeId];
    }

    return next;
  });

  return { ...data, nodes, edges };
}
